import { AnimatePresence, motion } from 'framer-motion';
import { Check, ListTodo, Plus, Star, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { usePolling } from '../../hooks/usePolling';
import { ApiError, endpoints, type TodoTask } from '../../services/api';
import { startMicrosoft, useSession } from '../../store/session';
import { useSettings } from '../../store/settings';

/** Microsoft To Do lists from the linked account – read/write, refreshed every 2 minutes. */
export function TodoLists({ interactive = true }: { interactive?: boolean }) {
  const microsoft = useSession((s) => s.microsoft);
  const hidden = useSettings((s) => s.lists.microsoftHidden);
  const canRead = Boolean(microsoft?.connected && microsoft.tasks);
  const lists = usePolling(canRead ? 'todo-lists' : null, useCallback(() => endpoints.todoLists().then((r) => r.lists), []), 10 * 60_000);
  const visible = (lists.data ?? []).filter((l) => !hidden.includes(l.id));
  const [activeId, setActiveId] = useState<string | null>(null);
  const active = visible.find((l) => l.id === activeId) ?? visible[0];

  if (!microsoft?.connected || !microsoft.tasks) {
    return (
      <div className="glass space-y-2 p-4 text-sm">
        <p className="muted">
          {microsoft?.connected
            ? 'Allow KCL Canvas to read your Microsoft To Do lists by reconnecting your Microsoft account.'
            : 'Connect your Microsoft account in Settings → Account to show your To Do lists.'}
        </p>
        {microsoft?.connected && (
          <button className="text-[var(--accent)] hover:underline" onClick={() => startMicrosoft('link')}>
            Reconnect Microsoft
          </button>
        )}
      </div>
    );
  }
  if (!active) {
    return <div className="glass muted p-4 text-sm">{lists.loading ? 'Loading To Do…' : 'No To Do lists to show.'}</div>;
  }

  return (
    <div className="glass p-4">
      <div className="mb-3 flex flex-wrap gap-1" role="tablist">
        {visible.map((l) => (
          <button
            key={l.id}
            role="tab"
            aria-selected={l.id === active.id}
            onClick={() => setActiveId(l.id)}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-sm transition ${
              l.id === active.id ? 'bg-[var(--accent)] text-black' : 'opacity-70 hover:opacity-100'
            }`}
          >
            <ListTodo className="h-4 w-4" aria-hidden /> {l.name}
          </button>
        ))}
      </div>
      <TaskList key={active.id} listId={active.id} interactive={interactive} />
    </div>
  );
}

function TaskList({ listId, interactive }: { listId: string; interactive: boolean }) {
  const refresh = useSession((s) => s.refreshMicrosoft);
  const load = useCallback(() => endpoints.todoTasks(listId).then((r) => r.tasks), [listId]);
  const remote = usePolling(listId, load, 2 * 60_000);
  const [tasks, setTasks] = useState<TodoTask[]>([]);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (remote.data) setTasks(remote.data);
  }, [remote.data]);

  const fail = (e: unknown) => {
    if (e instanceof ApiError && (e.code === 'needs_permission' || e.code === 'not_connected')) void refresh();
    setError(e instanceof Error ? e.message : 'Couldn’t update To Do');
  };

  const add = async (e: FormEvent) => {
    e.preventDefault();
    const title = text.trim();
    if (!title) return;
    setText('');
    const temp: TodoTask = { id: `tmp-${Date.now()}`, title, done: false, important: false, createdAt: new Date().toISOString(), completedAt: null, due: null };
    setTasks((t) => [...t, temp]);
    try {
      const { task } = await endpoints.todoAdd(listId, title);
      setTasks((t) => t.map((x) => (x.id === temp.id ? task : x)));
    } catch (err) {
      setTasks((t) => t.filter((x) => x.id !== temp.id));
      fail(err);
    }
  };

  const toggle = async (task: TodoTask) => {
    setTasks((t) => t.map((x) => (x.id === task.id ? { ...x, done: !x.done } : x)));
    try {
      await endpoints.todoSetDone(listId, task.id, !task.done);
    } catch (err) {
      setTasks((t) => t.map((x) => (x.id === task.id ? task : x)));
      fail(err);
    }
  };

  const remove = async (task: TodoTask) => {
    setTasks((t) => t.filter((x) => x.id !== task.id));
    try {
      await endpoints.todoDelete(listId, task.id);
    } catch (err) {
      setTasks((t) => [...t, task]);
      fail(err);
    }
  };

  const sorted = [...tasks].sort(
    (a, b) => Number(a.done) - Number(b.done) || Number(b.important) - Number(a.important) || (a.createdAt ?? '').localeCompare(b.createdAt ?? ''),
  );

  return (
    <div>
      {!remote.data && <div className="muted mb-2 text-sm">{remote.loading ? 'Loading…' : remote.error}</div>}
      {remote.data && sorted.length === 0 && <div className="muted mb-2 text-sm">Nothing here yet.</div>}
      <ul className="max-h-72 space-y-1 overflow-y-auto pr-1">
        <AnimatePresence initial={false}>
          {sorted.map((task) => (
            <motion.li
              key={task.id}
              layout
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: 40 }}
              transition={{ duration: 0.25 }}
              className="group flex items-center gap-2"
            >
              <button
                onClick={() => void toggle(task)}
                aria-label={task.done ? `Mark ${task.title} as not done` : `Mark ${task.title} as done`}
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition ${
                  task.done ? 'border-[var(--accent)] bg-[var(--accent)] text-black' : 'border-[var(--border)]'
                }`}
              >
                {task.done && <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden />}
              </button>
              <span className={`min-w-0 flex-1 truncate transition ${task.done ? 'muted line-through' : ''}`}>{task.title}</span>
              {task.important && <Star className="h-3.5 w-3.5 fill-current text-amber-300" aria-label="Important" />}
              {interactive && (
                <button
                  onClick={() => void remove(task)}
                  aria-label={`Delete ${task.title}`}
                  className="opacity-0 transition group-hover:opacity-60 hover:!opacity-100 focus:opacity-100"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </button>
              )}
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>
      {interactive && (
        <form onSubmit={add} className="mt-3 flex gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a task…"
            maxLength={255}
            className="min-w-0 flex-1 rounded-lg border border-[var(--border)] bg-transparent px-3 py-1.5 text-sm outline-none"
            aria-label="New task"
          />
          <button type="submit" className="rounded-lg bg-[var(--accent)] px-2 text-black" aria-label="Add task">
            <Plus className="h-4 w-4" aria-hidden />
          </button>
        </form>
      )}
      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
    </div>
  );
}
